import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import get from 'lodash/get';
import styles from './CellGallery.scss';
import getCellGallerySwiperParams from './cellGalleryParams';
import CellGalleryItem from './CellGalleryItem';
import MemoizedGallerySwiper from './MemoizedGallerySwiper';
import { getReadyImages } from '../../../../../utils/normalizeMediaStatus';

export default class CellGalleryWithDotsPagination extends Component {
  state = {
    activeIndex: 0
  };

  componentDidMount() {
    window.addEventListener('orientationchange', this.resizeThrottler, false);
    window.addEventListener('resize', this.resizeThrottler);
    if (this.swiperRef && this.swiperRef.swiper) {
      this.swiperRef.swiper.update();
      this.resizeThrottler();
    }
  }

  shouldComponentUpdate(newProps, newState) {
    const isRtlChanged = newProps.isRtl !== this.props.isRtl;
    const isImagesChanged = newProps.images.length !== this.props.images.length
      || newProps.images?.some((image, idx) => get(image, 'status') !== get(this.props.images?.[idx], 'status'));
    const isActiveIndexChanged =
      newState.activeIndex !== this.state.activeIndex &&
      !newProps.withoutPagination;

    return (
      isRtlChanged ||
      isImagesChanged ||
      newProps.withoutLoop ||
      isActiveIndexChanged
    );
  }

  componentWillUnmount() {
    window.removeEventListener(
      'orientationchange',
      this.resizeThrottler,
      false
    );
    window.removeEventListener('resize', this.resizeThrottler);
  }

  setSwiperRef = (ref) => {
    this.swiperRef = ref;
  };

  resizeThrottler = () => {
    this.swiperRef.swiper.resize.resizeHandler();
  };

  handleTransitionEnd = () => {
    if (this.swiperRef && this.swiperRef.swiper) {
      const idx = this.swiperRef.swiper.realIndex;

      if (this.props.onSlideChange) {
        this.props.onSlideChange();
      }

      if (Number.isInteger(idx)) {
        this.setState({ activeIndex: this.swiperRef.swiper.realIndex });
      }
    }
  };

  goToSlide = (index) => {
    if (this.swiperRef && this.swiperRef.swiper) {
      this.swiperRef.swiper.slideTo(index);
    }
  };

  handleUpdateSwiper = () => {
    if (this.swiperRef && this.swiperRef.swiper) {
      this.swiperRef.swiper.update();
      this.resizeThrottler();
      this.setState({ activeIndex: 0 });
    }
  };

  render() {
    const {
      isRtl,
      isLazy,
      withoutLoop,
      withoutAutoplay
    } = this.props;
    const images = getReadyImages(this.props.images);

    const gallerySwiperParams = getCellGallerySwiperParams({
      className: 'cellGalleryArrow',
      isMobile: true,
      isRtl,
      isLazy,
      withoutLoop
    });

    const params = {
      ...gallerySwiperParams,
      rtl: isRtl,
      noSwiping: true,
      initialSlide: 0,
      loop: !withoutLoop,
      on: {
        slideChangeTransitionEnd: this.handleTransitionEnd
      },
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
        dynamicBullets: images.length > 5
      },
      autoplay: !withoutAutoplay && {
        delay: 3000,
        disableOnInteraction: false
      },
      preloadImages: false,
      lazy: { loadOnTransitionStart: true }
    };

    const id = this.props.id ? `nearby-cell-${this.props.id}` : undefined;

    return (
      <div id={id} className={cx(styles.galleryWrapper, styles.dotsSlider, this.props.className)}>
        {(this.props.withoutRealEstateSlider || images.length <= 1) ? (
          <CellGalleryItem item={images[0] || {}} />
        ) : (
          <div className={styles.slider}>
            <MemoizedGallerySwiper
              onUpdate={this.handleUpdateSwiper}
              setSwiperRef={this.setSwiperRef}
              images={images}
              {...params}
            />
          </div>
        )}
      </div>
    );
  }
}

CellGalleryWithDotsPagination.propTypes = {
  images: PropTypes.array,
  isRtl: PropTypes.bool,
  withoutLoop: PropTypes.bool,
  withoutPagination: PropTypes.bool,
  isLazy: PropTypes.bool,
  className: PropTypes.string,
  withoutRealEstateSlider: PropTypes.bool,
  withoutAutoplay: PropTypes.bool,
  onSlideChange: PropTypes.func,
  root: PropTypes.string,
  id: PropTypes.string,
  isMobile: PropTypes.bool
};
